import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import useSWR from "swr";
import { END_POINTS } from "constants/APIConstant";
import { REGIONAL_OFFICE } from "constants/ElementsConstant";
import {
  configureLocations,
  EMPTY_ADDITIONAL_OFFICE,
  parseAdditionalOffice,
  sortGovernorates,
} from "./utils";

const DEFAULT_FILTERS = {
  governorateId: null,
  isMainLocation: false,
};

export function useContactUsData() {
  const dir = useSelector(({ theme }) => theme.direction);
  const currentLocale = useSelector(({ theme }) => theme.locale);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);

  const branchesDetailsFetching = useSWR({
    url: `${END_POINTS.REGIONAL_OFFICE_SEARCH}`,
    params: filters,
  });
  const mainBranchDetailsFetching = useSWR({
    url: `${END_POINTS.REGIONAL_OFFICE_MAIN_BRANCH}`,
  });
  const additionalOfficeFetching = useSWR({
    url: `${END_POINTS.ADDITIONAL_OFFICE}`,
  });
  const coutriesFetching = useSWR({
    url: `${END_POINTS.GOVERNORATES}`,
  });

  const headQuarter = mainBranchDetailsFetching.data?.data?.[0] ?? null;

  const headQuarterLocations = useMemo(
    () => configureLocations(mainBranchDetailsFetching),
    [mainBranchDetailsFetching.data, mainBranchDetailsFetching.error, mainBranchDetailsFetching.isLoading],
  );

  const branchLocations = useMemo(
    () => configureLocations(branchesDetailsFetching),
    [branchesDetailsFetching.data, branchesDetailsFetching.error, branchesDetailsFetching.isLoading],
  );

  const governorates = useMemo(() => {
    if (coutriesFetching.isLoading || coutriesFetching.error) return [];
    return sortGovernorates(coutriesFetching.data?.data, dir);
  }, [coutriesFetching.data, coutriesFetching.error, coutriesFetching.isLoading, dir]);

  const regionalOffices = useMemo(() => {
    if (
      branchesDetailsFetching.error ||
      branchesDetailsFetching.isLoading ||
      !branchesDetailsFetching.data ||
      coutriesFetching.isLoading
    ) {
      return [];
    }
    return branchesDetailsFetching.data.data || [];
  }, [
    branchesDetailsFetching.data,
    branchesDetailsFetching.error,
    branchesDetailsFetching.isLoading,
    coutriesFetching.isLoading,
  ]);

  const additionalOffices = useMemo(() => {
    if (
      additionalOfficeFetching.data === undefined ||
      additionalOfficeFetching.error
    ) {
      return {
        informationBank: EMPTY_ADDITIONAL_OFFICE,
        publicRelation: EMPTY_ADDITIONAL_OFFICE,
        nationalCenter: EMPTY_ADDITIONAL_OFFICE,
        humanResources: EMPTY_ADDITIONAL_OFFICE,
      };
    }
    const data = additionalOfficeFetching.data;
    return {
      informationBank: parseAdditionalOffice(
        data,
        REGIONAL_OFFICE.INFORMATION_BANK.key,
      ),
      publicRelation: parseAdditionalOffice(
        data,
        REGIONAL_OFFICE.PUBLIC_RELATION.key,
      ),
      nationalCenter: parseAdditionalOffice(
        data,
        REGIONAL_OFFICE.NATIONAL_CENTER_STATISTICAL_TRAINING.key,
      ),
      humanResources: parseAdditionalOffice(
        data,
        REGIONAL_OFFICE.HUMAN_RESOURCES_DEVELOPMENT_CENTER.key,
      ),
    };
  }, [additionalOfficeFetching.data, additionalOfficeFetching.error]);

  const onFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value === 0 || value === "0" ? null : value,
    }));
  };

  const isLoading =
    mainBranchDetailsFetching.isLoading ||
    additionalOfficeFetching.isLoading;

  return {
    dir,
    currentLocale,
    filters,
    onFilterChange,
    isLoading,
    headQuarter,
    headQuarterLocations,
    branchLocations,
    governorates,
    regionalOffices,
    additionalOffices,
  };
}
